import { useMemo } from 'react';

import { L3_CONTENT } from 'Deer/constants/L3RenderMap';
import { L1_PATHS, L1_TEAM_PATHS, L1_VALUES, L2_PATHS, L3_PATHS } from 'Deer/constants/routes';
import { type L3_OPTION } from 'Deer/constants/types/L3Content';
import { isEmpty } from 'lodash';
import { matchPath, useLocation } from 'react-router-dom';

import { useNonDeerRouteDetails } from './useNonDeerRouteDetails';

const getL3Options = ({
  l1Module,
  l2Module,
  l3Values,
  isTeamRoute,
  teamType,
  includeHidden = false,
}: {
  l1Module: keyof typeof L1_VALUES;
  l2Module:
    | 'review'
    | 'oneOnOne'
    | 'engagement'
    | 'goal'
    | 'initiative'
    | 'develop'
    | 'overview'
    | 'employee'
    | 'disciplinaryActions';
  l3Values: Record<string, string>;
  isTeamRoute: boolean;
  teamType: keyof typeof L1_TEAM_PATHS;
  includeHidden?: boolean;
}) => {
  if (isTeamRoute) {
    const teamRouteL3Options: Array<L3_OPTION> = Object.keys(l3Values)
      .filter((key) => (includeHidden ? true : L3_CONTENT.team[teamType][l2Module][key]?.hide?.() !== true))
      .map((key) => ({
        label: L3_CONTENT.team[teamType][l2Module][key]?.label,
        value: l3Values[key],
        content: L3_CONTENT.team[teamType][l2Module][key].content,
        cohortId: L3_CONTENT.team[teamType][l2Module][key].cohortId,
        type: L3_CONTENT.team[teamType][l2Module][key].type,
        headerLabel: L3_CONTENT.team[teamType][l2Module][key].headerLabel,
        teamId: L3_CONTENT.team[teamType][l2Module][key].teamId,
        hideFromTabList: L3_CONTENT.team[teamType][l2Module][key].hideFromTabList,
      }));
    return teamRouteL3Options;
  }

  const nonTeamRouteL3Options: Array<L3_OPTION> = Object.keys(l3Values)
    .filter((key) => (includeHidden ? true : L3_CONTENT[L1_VALUES[l1Module]][l2Module][key]?.hide?.() !== true))
    .map((key) => ({
      label: L3_CONTENT[L1_VALUES[l1Module]][l2Module][key].label,
      value: l3Values[key],
      content: L3_CONTENT[L1_VALUES[l1Module]][l2Module][key].content,
      cohortId: L3_CONTENT[L1_VALUES[l1Module]][l2Module][key].cohortId,
      type: L3_CONTENT[L1_VALUES[l1Module]][l2Module][key].type,
      headerLabel: L3_CONTENT[L1_VALUES[l1Module]][l2Module][key].headerLabel,
      teamId: L3_CONTENT[L1_VALUES[l1Module]][l2Module][key].teamId,
      hideFromTabList: L3_CONTENT[L1_VALUES[l1Module]][l2Module][key]?.hideFromTabList,
    }));

  return nonTeamRouteL3Options;
};

// REFER: https://pavsaund.com/post/2022-02-23-dynamic-breadcrumbs-and-routes-with-react-router/
export const useRouteToNavLevels = ({ includeHidden = false }: { includeHidden?: boolean } = {}) => {
  const location = useLocation();

  const l1Module = Object.keys(L1_PATHS).find((module) =>
    matchPath(location.pathname, { path: L1_PATHS[module], exact: false })
  ) as keyof typeof L1_VALUES;

  const l1Value = L1_VALUES[l1Module];

  const teamType: keyof typeof L1_TEAM_PATHS = Object.keys(L1_TEAM_PATHS).find((module) =>
    matchPath(location.pathname, { path: L1_TEAM_PATHS[module], exact: false })
  ) as keyof typeof L1_TEAM_PATHS;

  const isTeamRoute = l1Value === 'team';

  const possibleL2Routes = l1Value && (isTeamRoute ? L2_PATHS.team[teamType] : L2_PATHS[l1Value]);

  // Find the active L2 based on the current route
  const l2Module = isEmpty(possibleL2Routes)
    ? undefined
    : (Object.keys(possibleL2Routes).find((module) =>
        matchPath(location.pathname, { path: possibleL2Routes[module], exact: false })
      ) as keyof typeof possibleL2Routes);

  const l3Values =
    // eslint-disable-next-line no-nested-ternary
    l1Module && l2Module
      ? isTeamRoute
        ? L3_PATHS.team[teamType][l2Module]
        : L3_PATHS[L1_VALUES[l1Module]][l2Module]
      : {};
  // const applicableContentValues = l1Module && l2Module ? L3_CONTENT[L1_VALUES[l1Module]][l2Module] : {};
  const l3Options = getL3Options({ l1Module, l2Module, l3Values, isTeamRoute, teamType, includeHidden });

  const memoizedValues = useMemo(
    () => ({
      l1Module,
      l2Module,
      l3Options,
      teamType,
    }),
    // don't update the dependency array!!! otherwise it causes lot of App wide re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname]
  );

  return memoizedValues;
};

export const useRouteToMyTeamLevels = ({ includeHidden = false }: { includeHidden?: boolean } = {}) => {
  const location = useLocation();
  const l1Module = 'team';

  //   // Find the active L1 based on the current route
  const teamType: keyof typeof L1_TEAM_PATHS = Object.keys(L1_TEAM_PATHS).find((module) =>
    matchPath(location.pathname, { path: L1_TEAM_PATHS[module], exact: false })
  ) as keyof typeof L1_TEAM_PATHS;

  const possibleL2Routes = L2_PATHS.team[teamType] ?? {};

  // Find the active L2 based on the current route
  const l2Module:
    | 'review'
    | 'oneOnOne'
    | 'engagement'
    | 'goal'
    | 'initiative'
    | 'develop'
    | 'overview'
    | 'employee'
    | 'disciplinaryActions'
    | 'compensation' = Object.keys(possibleL2Routes).find((module) =>
    matchPath(location.pathname, { path: possibleL2Routes[module], exact: false })
  ) as keyof typeof possibleL2Routes;

  const l3Values = l1Module && l2Module ? L3_PATHS.team[teamType][l2Module] : {};
  // const applicableContentValues = l1Module && l2Module ? L3_CONTENT.team[teamType][l2Module] : {};
  const l3Options: Array<L3_OPTION> = Object.keys(l3Values)
    .filter((key) => L3_CONTENT.team[teamType][l2Module][key]?.hide?.() !== true)
    .map((key) => ({
      label: L3_CONTENT.team[teamType][l2Module][key]?.label,
      value: l3Values[key],
      content: L3_CONTENT.team[teamType][l2Module][key].content,
      cohortId: L3_CONTENT.team[teamType][l2Module][key].cohortId,
      type: L3_CONTENT.team[teamType][l2Module][key].type,
      headerLabel: L3_CONTENT.team[teamType][l2Module][key].headerLabel,
      teamId: L3_CONTENT.team[teamType][l2Module][key].teamId,
      hideFromTabList: L3_CONTENT.team[teamType][l2Module][key].hideFromTabList,
    }));

  const memoizedValues = useMemo(
    () => ({
      l1Module,
      l2Module,
      l3Options,
      teamType,
    }),
    // don't update the dependency array!!! otherwise it causes lot of App wide re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname]
  );

  return memoizedValues;
};

// this hook is used to check if the current route is a valid module route
// it is used to show the module not enabled screen instead of 404 page
export const useIsValidModuleRoute = () => {
  const { l2Module, l3Options, teamType } = useRouteToNavLevels({ includeHidden: true });
  const { l2Module: l2ModuleMyTeam, l3Options: l3OptionsMyTeam } = useRouteToMyTeamLevels({ includeHidden: true });

  // for non deer routes
  const matchedNonDeerRoute = useNonDeerRouteDetails();

  const l3OptionsToMatch = teamType ? l3OptionsMyTeam : l3Options;

  // match if location.pathname is in l3Options array using react router matchPath
  const isMatch = matchPath(location.pathname, { path: l3OptionsToMatch.map((option) => option.value), exact: false });

  return isMatch || matchedNonDeerRoute;
};
